/**
 * Event bridge for VIEWER role 403s.
 *
 * Rationale: when the backend returns 403 because the acting user is a
 * VIEWER, we want one consistent UX everywhere — a single toast saying
 * "Viewers cannot modify workspace data" — instead of every mutation
 * surface inventing its own generic "Import failed" / "Update failed"
 * message that hides the real cause.
 *
 * Contract:
 *   - `api.service` interceptor emits `emitViewerForbidden(message)` when
 *     it sees a 403 with code `VIEWER_FORBIDDEN` or `AUTHORIZATION_ERROR`
 *     (the second is the code emitted by the `requireWriteAccess`
 *     middleware on a viewer mutation attempt).
 *   - `ViewerForbiddenToastBridge` mounts once at the app root and
 *     subscribes to the event, raising a single toast. It dedupes
 *     bursts (e.g. a bulk-upload that fires N parallel requests, each
 *     of which would otherwise produce its own toast).
 *
 * SSR-safe: any environment without `window` no-ops.
 */

const EVENT_NAME = 'bower:viewer-forbidden';

export interface ViewerForbiddenDetail {
  /** Server-supplied human-readable message, if any. */
  readonly message?: string;
}

type ViewerForbiddenEvent = CustomEvent<ViewerForbiddenDetail>;

export function emitViewerForbidden(message?: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<ViewerForbiddenDetail>(EVENT_NAME, {
      detail: { message },
    }),
  );
}

/**
 * Subscribe to viewer-forbidden events. Returns a cleanup function for
 * React `useEffect`.
 */
export function subscribeViewerForbidden(
  handler: (detail: ViewerForbiddenDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event) => {
    const custom = event as ViewerForbiddenEvent;
    handler(custom.detail);
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
